<template>
  <div v-if="success">
    <div class="py-lg text-center md:py-xl">
      <h1 class="fw-book mt-0 mb-lg">{{ 'Thank you for registering' | t }}</h1>
      <p class="m-0">{{ 'Your Sloan product is now registered.' | t }}</p>
      <p class="m-0">{{ 'Find compatible add-ons to get the most out of your Sloan products.' | t }}</p>
    </div>
  </div>
  <div v-else class="max-w-2xl mx-auto">
    <div class="py-lg text-center md:py-xl">
      <h1 class="fw-book mt-0 mb-lg">{{ 'Register your Sloan product' | t }}</h1>
      <p>{{ 'Let’s make it official! Register your Sloan products so we can more easily help you if you need replacement parts or help troubleshooting. Simply complete the form below and provide the 12-digit serial number located on the products. To locate your serial number and for more information, see our Product Registration FAQ. Note, this is form is for U.S. customers only.' | t }}</p>
      <p>{{ 'For more about how we use your information, please see our' | t }} <a href="https://legal.sloan.com/#privacy-policy" target="_blank">{{ 'Privacy Policy' | t }}</a>.</p>
      <p>{{ '* required fields' | t }}</p>
    </div>
    <div class="py-lg md:py-xl">
      <form method="post" action="#" @submit.prevent="handleSubmit" class="cf-turnstile-verify">
        <h2 class="text-xl fw-strong">{{ 'General Information' | t }}</h2>
        <div class="grid grid-cols-1 gap-md mt-md sm:grid-cols-2 sm:gap-lg sm:mt-lg">
          <SloField>
            <SloLabel>{{ 'First Name' | t }} *</SloLabel>
            <SloInput
              v-model="generalFirstName"
              name="name"
              type="text"
              inputmode="name"
              :placeholder="'First Name' | t"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Last Name' | t }} *</SloLabel>
            <SloInput
              v-model="generalLastName"
              name="name"
              type="text"
              inputmode="name"
              :placeholder="'Last Name' | t"
              required
            />
          </SloField>
        </div>
        <div class="grid grid-cols-1 gap-md mt-md sm:grid-cols-2 sm:gap-lg sm:mt-lg">
          <SloField>
            <SloLabel>{{ 'Email' | t }} *</SloLabel>
            <SloInput
              v-model="generalEmail"
              name="email"
              type="email"
              inputmode="email"
              :placeholder="'Your Email Address' | t"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Phone' | t }} *</SloLabel>
            <SloInput
              v-model="generalPhone"
              name="phone"
              type="tel"
              inputmode="tel"
              placeholder="XXX-XXX-XXXX"
              required
            />
          </SloField>
        </div>
        <div class="grid grid-cols-1 gap-md mt-md sm:grid-cols-2 sm:gap-lg sm:mt-lg">
          <SloField>
            <SloLabel>{{ 'Please select the role that best applies' | t }}</SloLabel>
            <SloSelect
              v-model="generalRole"
              name="role"
              :placeholder="'Select your role' | t"
            >
              <option value="Architect">{{ 'Architect' | t }}</option>
              <option value="Building Owner">{{ 'Building Owner' | t }}</option>
              <option value="Contractor">{{ 'Contractor' | t }}</option>
              <option value="Designer">{{ 'Designer' | t }}</option>
              <option value="Distributor">{{ 'Distributor' | t }}</option>
              <option value="Engineer">{{ 'Engineer' | t }}</option>
              <option value="Facility Manager">{{ 'Facility Manager' | t }}</option>
              <option value="Plumber">{{ 'Plumber' | t }}</option>
              <option value="Property Manager">{{ 'Property Manager' | t }}</option>
              <option value="Real Estate">{{ 'Real Estate' | t }}</option>
              <option value="Manufacturer’s Representative">{{ 'Manufacturer’s Representative' | t }}</option>
              <option value="Wholesaler">{{ 'Wholesaler' | t }}</option>
              <option value="Other">{{ 'Other' | t }}</option>
            </SloSelect>
          </SloField>
          <SloField v-if="generalRole === 'Other'" class="sm:col-span-2">
            <SloLabel>{{ 'Please specify' | t }}</SloLabel>
            <SloInput
              v-model="generalRoleOther"
              name="role-other"
              type="text"
              inputmode="text"
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'What is your primary relationship to Sloan?' | t }}</SloLabel>
            <SloSelect
              v-model="generalRelationship"
              name="relationship"
              :placeholder="'– None –' | t"
            >
              <option value="Specify">{{ 'I specify products' | t }}</option>
              <option value="End User">{{ 'I purchase products' | t }}</option>
              <option value="Install">{{ 'I install products' | t }}</option>
              <option value="Sell">{{ 'I sell products' | t }}</option>
            </SloSelect>
          </SloField>
        </div>
        <div class="mt-md sm:mt-lg">
          <SloField>
            <div class="flex flex-row items-center gap-x-sm">
              <SloInput
                v-model="generalOptIn"
                name="optIn"
                type="checkbox"
                :checked="generalOptIn"
              />
              <SloLabel>
                {{ 'Opt-in for the latest information on commercial restroom trends' | t }}
              </SloLabel>
            </div>
          </SloField>
        </div>
        <div class="pt-lg sm:pt-xl" aria-label="spacer">
          <hr class="m-0">
        </div>
        <div class="pt-lg md:pt-xl">
          <h2 class="text-xl fw-strong">{{ 'Installation Details' | t }}</h2>
        </div>
        <div class="grid grid-cols-1 gap-md mt-md sm:grid-cols-2 sm:gap-lg sm:mt-lg">
          <SloField>
            <SloLabel>{{ 'Type of Installation' | t }} *</SloLabel>
            <SloSelect
              v-model="installationType"
              name="installation-type"
              :placeholder="'Select installation type' | t"
              required
            >
              <option value="Airport">{{ 'Airport' | t }}</option>
              <option value="Education">{{ 'Education' | t }}</option>
              <option value="Entertainment">{{ 'Entertainment' | t }}</option>
              <option value="Hospital & Healthcare">{{ 'Hospital & Healthcare' | t }}</option>
              <option value="Hotel & Hospitality">{{ 'Hotel & Hospitality' | t }}</option>
              <option value="Industrial/Manufacturing">{{ 'Industrial/Manufacturing' | t }}</option>
              <option value="Office Building">{{ 'Office Building' | t }}</option>
              <option value="Retail/Restaurant">{{ 'Retail/Restaurant' | t }}</option>
              <option value="Other">{{ 'Other' | t }}</option>
            </SloSelect>
          </SloField>
          <SloField v-if="installationType === 'Other'" class="sm:col-span-2">
            <SloLabel>{{ 'Please specify' | t }} *</SloLabel>
            <SloInput
              v-model="installationTypeOther"
              name="installation-other"
              type="text"
              inputmode="text"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Installation Location / Site Name' | t }} *</SloLabel>
            <SloInput
              v-model="installationLocation"
              name="installation-location"
              type="text"
              inputmode="text"
              :placeholder="'Building or property name' | t"
              required
            />
          </SloField>
        </div>
        <div class="grid grid-cols-1 gap-md mt-md sm:grid-cols-2 sm:gap-lg sm:mt-lg">
          <SloField>
            <SloLabel>{{ 'Site Street Address' | t }} *</SloLabel>
            <SloInput
              v-model="installationAddress"
              name="address"
              type="text"
              inputmode="text"
              :placeholder="'123 Street Name' | t"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'City' | t }} *</SloLabel>
            <SloInput
              v-model="installationCity"
              name="city"
              type="text"
              inputmode="text"
              :placeholder="'Your city' | t"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Postal Code' | t }} *</SloLabel>
            <SloInput
              v-model="installationPostal"
              name="zip"
              type="text"
              inputmode="text"
              placeholder="12345"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Country' | t }} *</SloLabel>
            <SloSelect
              v-model="installationCountry"
              name="country"
              :placeholder="'Select country' | t"
              required
            >
              <option value="United States of America">United States of America</option>
              <option value="Afganistan">Afganistan</option>
              <option value="Albania">Albania</option>
              <option value="American Samoa">American Samoa</option>
              <option value="Anguilla">Anguilla</option>
              <option value="Antigua & Barbuda">Antigua & Barbuda</option>
              <option value="Argentina">Argentina</option>
              <option value="Armenia">Armenia</option>
              <option value="Aruba">Aruba</option>
              <option value="Australia">Australia</option>
              <option value="Bermuda">Bermuda</option>
              <option value="Bolivia">Bolivia</option>
              <option value="Canada">Canada</option>
              <option value="Chile">Chile</option>
              <option value="China">China</option>
              <option value="Costa Rica">Costa Rica</option>
              <option value="Ecuador">Ecuador</option>
              <option value="Egypt">Egypt</option>
              <option value="France">France</option>
              <option value="Guatemala">Guatemala</option>
              <option value="Honduras">Honduras</option>
              <option value="Hong Kong">Hong Kong</option>
              <option value="India">India</option>
              <option value="Indonesia">Indonesia</option>
              <option value="Iraq">Iraq</option>
              <option value="Ireland">Ireland</option>
              <option value="Italy">Italy</option>
              <option value="Kuwait">Kuwait</option>
              <option value="Lebanon">Lebanon</option>
              <option value="Mexico">Mexico</option>
              <option value="Nicaragua">Nicaragua</option>
              <option value="Panama">Panama</option>
              <option value="Peru">Peru</option>
              <option value="Phillipines">Phillipines</option>
              <option value="Poland">Poland</option>
              <option value="Portugal">Portugal</option>
              <option value="Puerto Rico">Puerto Rico</option>
              <option value="Saudi Arabia">Saudi Arabia</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Thailand">Thailand</option>
              <option value="Uganda">Uganda</option>
              <option value="United Arab Erimates">United Arab Erimates</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Venezuela">Venezuela</option>
            </SloSelect>
          </SloField>
          <SloField v-if="installationCountry === 'United States of America'">
            <SloLabel>{{ 'State' | t }} *</SloLabel>
            <SloSelect
              v-model="installationState"
              name="state"
              :placeholder="'Select state' | t "
              required
            >
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="DC">District Of Columbia</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </SloSelect>
          </SloField>
          <SloField>
            <SloLabel>{{ 'Purchase Date' | t }} *</SloLabel>
            <SloInput
              v-model="installationPurchaseDate"
              name="purchase-date"
              type="date"
              inputmode="date"
              placeholder="MM/DD/YYYY"
              required
            />
          </SloField>
          <SloField>
            <SloLabel>{{ 'Installation Date' | t }}</SloLabel>
            <SloInput
              v-model="installationDate"
              name="installation-date"
              type="date"
              inputmode="date"
              placeholder="MM/DD/YYYY"
            />
          </SloField>
          <SloField class="sm:col-span-2">
            <SloLabel>{{ 'The bottle filler was installed by' | t }}</SloLabel>
            <SloSelect
              v-model="installationInstalledBy"
              name="installed-by"
              :placeholder="'Owner, Contractor, Plumber, etc.' | t"
            >
              <option value="Owner">{{ 'Owner' | t }}</option>
              <option value="Contractor">{{ 'Contractor' | t }}</option>
              <option value="Plumber">{{ 'Plumber' | t }}</option>
              <option value="Other">{{ 'Other' | t }}</option>
            </SloSelect>
          </SloField>
          <SloField v-if="installationInstalledBy === 'Other'" class="sm:col-span-2">
            <SloLabel>{{ 'Please specify' | t }}</SloLabel>
            <SloInput
              v-model="installationInstalledByOther"
              name="installation-other"
              type="text"
              inputmode="text"
              required
            />
          </SloField>
        </div>
        <div class="pt-lg sm:pt-xl" aria-label="spacer">
          <hr class="m-0">
        </div>
        <div class="pt-lg md:pt-xl">
          <h2 class="text-xl fw-strong">{{ 'Product Details' | t }}</h2>
          <div>
            <TransitionGroup name="fade-in" tag="div" class="grid grid-cols-1 gap-y-md mt-md sm:mt-lg">
              <div
                v-for="(field, index) in productsInstalled"
                :key="index"
                class="registration--product-detail grid grid-cols-1 gap-md sm:grid-cols-2 sm:gap-lg"
              >
                <SloField>
                  <SloLabel>{{ 'Model Number' | t }} *</SloLabel>
                  <SloSelect
                    v-model="field.modelNumber"
                    :name="'model-number' + index"
                    :placeholder="'Select Model Number' | t"
                    required
                  >
                    <option value="DRS100">DRS100</option>
                    <option value="DRS110">DRS110</option>
                    <option value="DRS120">DRS120</option>
                  </SloSelect>
                </SloField>
                <SloField>
                  <SloLabel>{{ 'Serial Number' | t }} *</SloLabel>
                  <SloInput
                    v-model="field.serialNumber"
                    :name="'serial-number' + index"
                    type="text"
                    inputmode="text"
                    placeholder="0000000"
                    required
                  />
                </SloField>
                <SloButton
                  v-if="field.isDuplicated"
                  @click="removeProductDetail(index)"
                  fill="clear"
                  class="registration--remove-product-detail p-0"
                >
                  <SloIcon name="CircleMinus" />
                </SloButton>
              </div>
            </TransitionGroup>
          </div>
          <div class="flex items-start justify-between flex-wrap gap-md mt-md">
            <SloButton
              @click="addProductDetail"
              type="button"
              fill="clear"
              class="flex items-center gap-sm p-0"
            >
              <SloIcon name="Plus" /> {{ 'Register additional product' | t }}
            </SloButton>
            <SloButton
              @click="showDiagramInit"
              type="button"
              fill="clear"
              class="flex items-center gap-sm p-0"
            >
              <SloIcon name="CircleInfo" /> {{ 'How to find your model and serial number.' | t }}
            </SloButton>
          </div>
          <transition name="fade-up">
            <div v-if="showDiagram" class="my-md p-md bg-gray-50 sm:my-lg sm:p-lg">
              <div class="grid grid-cols-2 gap-md">
                <figure class="col-span-2 registration--product-diagram">
                  <slot name="product-diagram" />
                </figure>
                <div class="col-span-2 grid grid-cols-2 gap-md lg:gap-lg">
                  <div>
                    <p>{{ 'Model Number' }}</p>
                    <div class="p-sm bg-white">
                      <p class="m-0">DSRXXX</p>
                    </div>
                  </div>
                  <div>
                    <p>{{ 'Serial Number' }}</p>
                    <div class="p-sm bg-white">
                      <p class="m-0"># 1234567</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
          <div class="grid grid-cols-1 gap-md mt-md sm:gap-lg sm:mt-lg">
            <SloField>
              <SloLabel>{{ 'Where did you purchase this product?' | t }}</SloLabel>
              <SloSelect
                v-model="productPurchasedWhere"
                name="purchased-where"
                :placeholder="'– None –' | t"
              >
                <option value="Amazon">{{ 'Amazon' | t }}</option>
                <option value="Wholesaler">{{ 'Wholesaler' | t }}</option>
                <option value="Online Retailer">{{ 'Online Retailer' | t }}</option>
                <option value="Sloan.com">{{ 'Sloan.com' | t }}</option>
              </SloSelect>
            </SloField>
            <SloField>
              <SloLabel>{{ 'Why did you choose this product?' | t }}</SloLabel>
              <SloSelect
                v-model="productPurchasedWhy"
                name="purchased-why"
                :placeholder="'– None –' | t"
              >
                <option value="Renovation">{{ 'Renovation (New Unit)' | t }}</option>
                <option value="Construction">{{ 'New Construction (New Unit)' | t }}</option>
                <option value="Replacement">{{ 'Replacement of Existing Unit' | t }}</option>
                <option value="Other">{{ 'Other' | t }}</option>
              </SloSelect>
            </SloField>
            <SloField v-if="productPurchasedWhy === 'Other'">
              <SloLabel>{{ 'Please specify' | t }}</SloLabel>
              <SloInput
                v-model="productPurchasedWhyOther"
                name="why-other"
                type="text"
                inputmode="text"
                required
              />
            </SloField>
            <SloField>
              <SloLabel>{{ 'What sources did you use to select this product?' | t }}</SloLabel>
              <SloSelect
                v-model="productPurchasedSources"
                name="sources"
                :placeholder="'– None –' | t"
              >
                <option value="Amazon">{{ 'Amazon' | t }}</option>
                <option value="Wholesaler">{{ 'Wholesaler' | t }}</option>
                <option value="Online Retailer">{{ 'Online Retailer' | t }}</option>
                <option value="Sloan.com">{{ 'Sloan.com' | t }}</option>
                <option value="Other">{{ 'Other' | t }}</option>
              </SloSelect>
            </SloField>
            <SloField v-if="productPurchasedSources === 'Other'">
              <SloLabel>{{ 'Please specify' | t }}</SloLabel>
              <SloInput
                v-model="productPurchasedSourcesOther"
                name="why-other"
                type="text"
                inputmode="text"
                required
              />
            </SloField>
          </div>
          <div class="grid grid-cols-2 pt-lg sm:grid-cols-3 sm:pt-xl">
            <div class="cf-turnstile-widget mb-md"></div>
            <SloButton type="submit" :disabled="isSubmitDisabled">
              <SloLoader :loading="loading" size="md" color="white">
                {{ 'Register my product' | t }}
              </SloLoader>
            </SloButton>
            <div v-if="error" class="mt-md col-span-full">
              <p class="c-red-400 text-base fw-strong" v-html="error" />
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import t from '../main/i18n';
import SloButton from '../../lib/components/SloButton.vue';
import SloField from '../../lib/components/SloField.vue';
import SloInput from '../../lib/components/SloInput.vue';
import SloLabel from '../../lib/components/SloLabel.vue';
import SloSelect from '../../lib/components/SloSelect.vue';
import SloIcon from '../../lib/components/SloIcon.vue';
import SloLoader from '../../lib/components/SloLoader.vue';

export default {
  name: 'ProductRegistration',
  components: {
    SloInput,
    SloField,
    SloLabel,
    SloSelect,
    SloButton,
    SloIcon,
    SloLoader
  },
  filters: {
    t
  },
  data() {
    return {
      loading: false,
      error: false,
      success: false,
      showDiagram: false,
      generalFirstName: null,
      generalLastName: null,
      generalEmail: null,
      generalPhone: null,
      generalRole: null,
      generalRoleOther: null,
      generalRelationship: null,
      generalOptIn: true,
      installationType: null,
      installationTypeOther: null,
      installationLocation: null,
      installationCountry: null,
      installationAddress: null,
      installationCity: null,
      installationState: null,
      installationPostal: null,
      installationPurchaseDate: null,
      installationDate: null,
      installationInstalledBy: null,
      installationInstalledByOther: null,
      productsInstalled: [{modelNumber: '', serialNumber: '', isDuplicated: false}],
      productPurchasedWhere: null,
      productPurchasedWhy: null,
      productPurchasedWhyOther: null,
      productPurchasedSources: null,
      productPurchasedSourcesOther: null
    };
  },
  computed: {
    isSubmitDisabled() {
      return this.loading
        || !this.generalEmail
        || !this.generalFirstName
        || !this.generalLastName
        || !this.installationType
        || this.installationType === 'Other' && !this.installationTypeOther
        || !this.installationLocation
        || !this.installationCountry
        || !this.installationAddress
        || !this.installationCity
        || (this.isCountryUS && !this.installationState)
        || !this.installationPostal
        || !this.installationPurchaseDate;
    },
    isCountryUS() {
      return this.installationCountry === 'United States of America';
    }
  },
  methods: {
    addProductDetail() {
      this.productsInstalled.push({
        modelNumber: '',
        serialNumber: '',
        isDuplicated: true
      });
    },
    removeProductDetail(index) {
      if (this.productsInstalled[index].isDuplicated) {
        this.productsInstalled.splice(index, 1);
      }
    },
    showDiagramInit() {
      this.showDiagram = !this.showDiagram;
    },
    handleSubmit() {
      this.loading = true;

      const formId = document.head.querySelector('meta[name="marketo-product-registration-form-id"]').content;
      // eslint-disable-next-line no-undef
      MktoForms2.loadForm('//info.sloan.com', '905-IOZ-761', formId, form => {
        // Required Fields
        let fields = {
          'Email': this.generalEmail,
          'FirstName': this.generalFirstName,
          'LastName': this.generalLastName,
          'Phone': this.generalPhone,
          'installationDetailsTypeofInstallation':
            this.installationTypeOther ? `Other: ${this.installationTypeOther}` : this.installationType,
          'installationDetailsSiteName': this.installationLocation,
          'installationDetailsAddress': this.installationAddress,
          'installationDetailsCity': this.installationCity,
          'installationDetailsCountry': this.installationCountry,
          'installationDetailsPostalCode': this.installationPostal,
          'installationDetailsPurchasedDates': this.installationPurchaseDate,
          'productDetailsModelNumber': this.productsInstalled.map(product => product.modelNumber).join(', '),
          'productDetailsSerialNumber': this.productsInstalled.map(product => product.serialNumber).join(', ')
        };

        // Optional Fields
        if (this.generalRole) fields['segment'] =
          this.generalRoleOther ? `Other: ${this.generalRoleOther}` : this.generalRole;
        if (this.generalRelationship) fields['jobDescription'] = this.generalRelationship;
        if (this.generalOptIn) fields['Unsubscribed'] = !this.generalOptIn;
        if (this.isCountryUS) fields['installationDetailsState'] = this.installationState;
        if (this.installationDate) fields['installationDetailsInstallationDate'] = this.installationDate;
        if (this.installationInstalledBy) fields['installationDetailsInstalledBy'] =
          this.installationInstalledByOther ? `Other: ${this.installationInstalledByOther}` : this.installationInstalledBy;
        if (this.productPurchasedWhere) fields['productDetailsPurchasedAt'] = this.productPurchasedWhere;
        if (this.productPurchasedWhy) fields['productDetailsPurchaseReason'] =
          this.productPurchasedWhyOther ? `Other: ${this.productPurchasedWhyOther}` : this.productPurchasedWhy;
        if (this.productPurchasedSources) fields['productDetailsPurchaseSources'] =
          this.productPurchasedSourcesOther ? `Other: ${this.productPurchasedSourcesOther}` : this.productPurchasedSources;

        form.vals(fields);
        form.validate();
        form.onValidate(status => {
          if (!status) {
            this.loading = false;
            this.success = false;
            this.error = t('Oops! Something went wrong. Please reach out to our support team for help.');
            return false;
          }
        });

        form.submit();
        form.onSuccess(() => {
          this.loading = false;
          this.error = false;
          this.success = true;
          window.scrollTo(0, 0);
          return false;
        });
      });
    }
  }
};
</script>

<style lang="scss">
.registration {
  &--product-detail {
    position: relative;
  }
  &--remove-product-detail {
    position: absolute;
    top: 0;
    right: 0;

    @media (min-width: 768px) {
      top: 60%;
      right: -32px;
      transform: translateY(-50%);
    }
  }
  &--product-diagram {
    img {
      width: 100%;
      height: auto;
    }
  }
}

.fade-in-enter, .fade-in-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.fade-in-enter-active, .fade-in-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
</style>
